import { queryOptions, skipToken } from '@tanstack/react-query'
import { AUTHZ_FRESHNESS_MS } from './authzFreshness'
import { fetchRecordAuthorization } from './recordAuthorizationFetch'
import { loadRecordPermission } from './recordPermissionBatchFetcher'
import type { AskedRecord, AskedRecordPermission } from './askedRecord'
import type { AuthzRollout } from './AuthzRolloutProvider'
import type { RecordPermissionsEntity } from '@omnea-digital/authz-model'
import type { QueryClient } from '@tanstack/react-query'

const RECORD_AUTHORIZATION_QUERY_KEY = 'RecordAuthorization'

export const recordAuthorizationQueryKey = (userId: string, entity: RecordPermissionsEntity, recordId: string) =>
  [RECORD_AUTHORIZATION_QUERY_KEY, userId, entity, recordId] as const

// Under the record's own key, so invalidating a record's answers clears its per-permission ones too.
const recordPermissionQueryKey = (userId: string, entity: RecordPermissionsEntity, recordId: string, permission: string) =>
  [...recordAuthorizationQueryKey(userId, entity, recordId), 'permission', permission] as const

/** Named where there is no record to ask about, and shaped so it cannot collide with a real answer. */
const NO_RECORD_ASKED_QUERY_KEY = [RECORD_AUTHORIZATION_QUERY_KEY, 'noRecord'] as const

/**
 * Off the service the record endpoints answer 404 and the role rules decide, which the rollout already says
 * without a request per record.
 */
export const isOnPermissionsService = (rollout: AuthzRollout): boolean => rollout.status === 'ready' && rollout.fgaAuthoritative

export const recordAuthorizationQueryOptions = <Entity extends RecordPermissionsEntity>(
  getToken: () => Promise<string>,
  userId: string,
  asked: AskedRecord<Entity> | null
) =>
  queryOptions({
    queryKey: asked == null ? NO_RECORD_ASKED_QUERY_KEY : recordAuthorizationQueryKey(userId, asked.entity, asked.recordId),
    queryFn: asked == null ? skipToken : () => fetchRecordAuthorization(getToken, asked),
    staleTime: AUTHZ_FRESHNESS_MS,
    gcTime: AUTHZ_FRESHNESS_MS,
  })

/** One permission on one record, batched with every row asking the same permission this tick. */
export const recordPermissionQueryOptions = <Entity extends RecordPermissionsEntity>(
  getToken: () => Promise<string>,
  userId: string,
  asked: AskedRecordPermission<Entity> | null
) =>
  queryOptions({
    queryKey:
      asked == null
        ? NO_RECORD_ASKED_QUERY_KEY
        : recordPermissionQueryKey(userId, asked.entity, asked.recordId, asked.permission),
    queryFn: asked == null ? skipToken : () => loadRecordPermission(getToken, userId, asked),
    staleTime: AUTHZ_FRESHNESS_MS,
    gcTime: AUTHZ_FRESHNESS_MS,
  })

export const isRecordAuthorizationQueryKey = (queryKey: readonly unknown[]): boolean =>
  queryKey[0] === RECORD_AUTHORIZATION_QUERY_KEY

/** Call after a write that changes who may do what to a record, such as a grant or a parent link; cached answers stay otherwise. */
export const invalidateRecordAuthorization = async (queryClient: QueryClient) => {
  const filters = { queryKey: [RECORD_AUTHORIZATION_QUERY_KEY] }
  // Invalidating refetches only the answers on screen. An answer still being fetched for a record nobody
  // shows would land afterwards and count as fresh, so it is cancelled first.
  await queryClient.cancelQueries(filters)
  await queryClient.invalidateQueries(filters)
}
